import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { filter, pipe, switchMap, tap } from 'rxjs';
import {
  DiagnosisSolutionCompatibility,
  DiagnosisSolutionCreateDto,
} from 'src/app/core/common/models/workflow/diagnosis-solution';
import { DiagnosisSolutionService } from 'src/app/core/services/diagnosis-solution.service';
import { StoreService } from 'src/app/core/services/global-store/store.service';
import { DialogCommonService } from '../../../../../core/services/common/dialog-common.service';
import {
  AvailableSolutionState,
  CompatibilityByCate,
} from './available-solutions.model';

const initialState: AvailableSolutionState = {
  diagnosisSolutions: [],
  isEnableUnselectAllBtn: false,
  solutionsByCategory: [],
  isShowLoadingSpinner: false,
  isAllSolutionDisabled: false,
};

export type AvailableSolutionStoreType = InstanceType<
  typeof AvailableSolutionStore
>;

export const AvailableSolutionStore = signalStore(
  { providedIn: 'root' },
  withDevtools('available-solutions'),
  withState(initialState),
  withComputed(({ solutionsByCategory }) => ({
    filteredSolutionsByCategory: computed(() => solutionsByCategory()),
  })),
  withMethods(
    (
      store,
      dialogCommonService = inject(DialogCommonService),
      diagnosisSolutionService = inject(DiagnosisSolutionService),
      storeService = inject(StoreService),
    ) => ({
      loadAvailableSolutions: rxMethod<string>(
        pipe(
          filter((diagnosisSessionId) => !!diagnosisSessionId),
          switchMap((diagnosisSessionId: string) =>
            diagnosisSolutionService.getAvailableSolutions(diagnosisSessionId),
          ),
          tapResponse(
            (availableSolutions) => {
              patchState(store, {
                diagnosisSolutions: availableSolutions,
              });
            },
            (error: HttpErrorResponse) => {
              console.error(
                'An error occurred in loadAvailableSolutions:',
                error,
              );
            },
          ),
        ),
      ),
      unselectAllSolutions: rxMethod<string>(
        pipe(
          tap(() =>
            patchState(store, {
              isShowLoadingSpinner: true,
            }),
          ),
          switchMap((diagnosisSessionId: string) =>
            diagnosisSolutionService.resetSolutionSelection(diagnosisSessionId),
          ),
          tapResponse(
            (diagnosisSolutions) => {
              patchState(store, {
                diagnosisSolutions,
                isEnableUnselectAllBtn: false,
                isShowLoadingSpinner: false,
              });
            },
            (error: HttpErrorResponse) => {
              dialogCommonService.show(true, {
                title: 'Blocking session',
                message: error.message,
                closeLabel: 'Close',
                closable: false,
              });
              patchState(store, {
                isShowLoadingSpinner: false,
              });
              console.error(
                'An error occurred in unselectAllSolutions:',
                error,
              );
            },
          ),
        ),
      ),
      selectOneSolution: rxMethod<DiagnosisSolutionCreateDto>(
        pipe(
          filter((selectedSolution) => !!selectedSolution),
          tap(() =>
            patchState(store, {
              isShowLoadingSpinner: true,
            }),
          ),
          switchMap((selectedSolution: DiagnosisSolutionCreateDto) =>
            diagnosisSolutionService.selectSolution(selectedSolution),
          ),
          tapResponse(
            (diagnosisSolutions) => {
              storeService.dispatchSelectedSolution(true);
              patchState(store, {
                diagnosisSolutions,
                isEnableUnselectAllBtn: true,
                isShowLoadingSpinner: false,
              });
            },
            (error: HttpErrorResponse) => {
              dialogCommonService.show(true, {
                title: 'Blocking session',
                message: error.message,
                closeLabel: 'Close',
                closable: false,
              });
              patchState(store, {
                isShowLoadingSpinner: false,
              });
              console.error('An error occurred in selectOneSolution:', error);
            },
          ),
        ),
      ),
      unselectOneSolution: rxMethod<DiagnosisSolutionCreateDto>(
        pipe(
          tap(() =>
            patchState(store, {
              isShowLoadingSpinner: true,
            }),
          ),
          switchMap((selectedSolution: DiagnosisSolutionCreateDto) =>
            diagnosisSolutionService.unselectSolution(selectedSolution),
          ),
          tapResponse(
            (diagnosisSolutions) => {
              patchState(store, {
                isShowLoadingSpinner: false,
                diagnosisSolutions,
              });
            },
            (error: HttpErrorResponse) => {
              dialogCommonService.show(true, {
                title: 'Blocking session',
                message: error.message,
                closeLabel: 'Close',
                closable: false,
              });
              patchState(store, {
                isShowLoadingSpinner: false,
              });
              console.error('An error occurred in unselectOneSolution:', error);
            },
          ),
        ),
      ),
      groupSolutionCompatibilities: rxMethod<DiagnosisSolutionCompatibility[]>(
        pipe(
          filter((compatibilities) => !!compatibilities.length),
          tapResponse(
            (compatibilities: DiagnosisSolutionCompatibility[]) => {
              const group = compatibilities.reduce(
                (
                  acc: { [key: string]: DiagnosisSolutionCompatibility[] },
                  current: DiagnosisSolutionCompatibility,
                ) => {
                  const key = current.availableSolution.solutionCategory.name;
                  if (!acc[key]) {
                    acc[key] = [];
                  }
                  acc[key].push(current);
                  return acc;
                },
                {},
              );

              const solutionsByCategory = Object.keys(group)
                .map((key) => ({
                  category: key,
                  compatibilities: group[key].sort(
                    (a, b) =>
                      a.availableSolution.orderBy! -
                      b.availableSolution.orderBy!,
                  ),
                }))
                .sort((a, b) => b.category.localeCompare(a.category));

              patchState(store, {
                solutionsByCategory,
              });
            },
            (error: HttpErrorResponse) => {
              console.error(
                'An error occurred in groupSolutionCompatibilities:',
                error,
              );
            },
          ),
        ),
      ),
      isSolutionSelected: rxMethod<CompatibilityByCate[]>(
        pipe(
          tapResponse(
            (solutionsByCategory: CompatibilityByCate[]) => {
              const isSolutionSelected = solutionsByCategory.some((item) =>
                item.compatibilities.some(
                  (compatibility) => compatibility.isChecked,
                ),
              );
              storeService.dispatchSelectedSolution(isSolutionSelected);
              patchState(store, {
                isEnableUnselectAllBtn: isSolutionSelected,
              });
            },
            (error: HttpErrorResponse) => {
              console.error('An error occurred in isSolutionSelected:', error);
            },
          ),
        ),
      ),
      disabledAllSolution(isAllSolutionDisabled: boolean): void {
        patchState(store, () => ({ isAllSolutionDisabled }));
      },
      loadAvailableSolutionsFromStore: rxMethod<void>(
        pipe(
          switchMap(() => storeService.getAvailableSolutions()),
          filter((diagnosisSolutions) => !!diagnosisSolutions.length),
          tapResponse(
            (diagnosisSolutions: DiagnosisSolutionCompatibility[]) => {
              patchState(store, {
                diagnosisSolutions,
              });
            },
            (error: HttpErrorResponse) => {
              console.error(
                'An error occurred in loadAvailableSolutionsFromStore:',
                error,
              );
            },
          ),
        ),
      ),
    }),
  ),
  withHooks({
    onInit({
      groupSolutionCompatibilities,
      isSolutionSelected,
      loadAvailableSolutionsFromStore,
      diagnosisSolutions,
      solutionsByCategory,
    }) {
      groupSolutionCompatibilities(diagnosisSolutions);
      isSolutionSelected(solutionsByCategory);
      loadAvailableSolutionsFromStore();
    },
  }),
);
